import { StorageError, StorageApiError, isStorageError } from './supabase/errors'
import { encodeObjectName } from './encoding'
import { Http } from './http'
import { Result, DownloadOptions, FetchParameters } from './types'

/** 把任意错误规整为 `StorageError`。 */
function toStorageError(error: any): StorageError {
  return isStorageError(error) ? error : new StorageError(error?.message || String(error))
}

/** 下载构建器内部共享上下文。 */
export interface DownloadContext {
  http: Http
  bucketId: string
  cleanName: string
  options?: DownloadOptions
  parameters?: FetchParameters
  /** 调用方实例级自定义请求头，会并入下载请求（条件请求头优先级更高）。 */
  headers?: Record<string, string>
  shouldThrowOnError: boolean
}

/** 由下载选项构建条件请求头与 query。 */
function buildHeadersAndQuery(options?: DownloadOptions): {
  headers: Record<string, string>
  query: Record<string, any>
} {
  const query: Record<string, any> = {}
  if (options?.download !== undefined) {
    query.download = options.download === true ? '' : options.download
  }
  if (options?.cacheNonce !== undefined) {
    query.cacheNonce = String(options.cacheNonce)
  }
  return { headers: {}, query }
}

/**
 * 流式下载构建器（两阶段下载的「流」终态）。
 *
 * 实现 `PromiseLike`，可直接 `await`，resolve 为 `Result<ReadableStream>`。
 * 由 `DownloadBuilder.asStream()` 创建，直接返回原生 `Response.body`。
 */
export class StreamDownloadBuilder implements PromiseLike<Result<ReadableStream<Uint8Array>>> {
  constructor(private ctx: DownloadContext) {}

  then<TResult1 = Result<ReadableStream<Uint8Array>>, TResult2 = never>(
    onfulfilled?: ((value: Result<ReadableStream<Uint8Array>>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected)
  }

  private async execute(): Promise<Result<ReadableStream<Uint8Array>>> {
    try {
      const { headers, query } = buildHeadersAndQuery(this.ctx.options)
      const { response } = await this.ctx.http.download({
        path: `/object/${this.ctx.bucketId}/${encodeObjectName(this.ctx.cleanName)}`,
        headers: { ...this.ctx.headers, ...headers },
        query,
        signal: this.ctx.parameters?.signal,
        cache: this.ctx.parameters?.cache,
      })
      if (!response.body) {
        throw new StorageApiError('Response body is empty', response.status, 'STORAGE_PG_EMPTY_BODY')
      }
      return { data: response.body, error: null }
    } catch (error: any) {
      if (this.ctx.shouldThrowOnError) throw error
      return { data: null, error: toStorageError(error) }
    }
  }
}

/**
 * 下载构建器（thenable）。
 *
 * 默认终态为 `Blob`：直接 `await bucket.download(path)` 即得 `Result<Blob>`，
 * 调用方式与返回普通 Promise 完全一致（惰性执行，`await` / `.then()` 时才真正发请求）。
 *
 * 链式 `.asStream()` 切换为流式终态，返回 `StreamDownloadBuilder`（resolve 为 `Result<ReadableStream>`）。
 * 此设计对齐 Supabase `download().asStream()` 的使用效果，但实现为自有的轻量 thenable，不依赖其内部类。
 *
 * @example
 * ```ts
 * // 默认：Blob
 * const { data: blob } = await bucket.download('a.png')
 * // 流式：ReadableStream（适合 Node 大文件，避免整体读入内存）
 * const { data: stream } = await bucket.download('big.zip').asStream()
 * ```
 */
export class DownloadBuilder implements PromiseLike<Result<Blob>> {
  constructor(private ctx: DownloadContext) {}

  /** 切换为流式下载终态，返回 `StreamDownloadBuilder`。 */
  asStream(): StreamDownloadBuilder {
    return new StreamDownloadBuilder(this.ctx)
  }

  then<TResult1 = Result<Blob>, TResult2 = never>(
    onfulfilled?: ((value: Result<Blob>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected)
  }

  private async execute(): Promise<Result<Blob>> {
    try {
      const { headers, query } = buildHeadersAndQuery(this.ctx.options)
      const { response } = await this.ctx.http.download({
        path: `/object/${this.ctx.bucketId}/${encodeObjectName(this.ctx.cleanName)}`,
        headers: { ...this.ctx.headers, ...headers },
        query,
        signal: this.ctx.parameters?.signal,
        cache: this.ctx.parameters?.cache,
      })
      // 直接复用原生 Response.blob()（与 Supabase 一致），content-type 由 Response 自动保留。
      return { data: await response.blob(), error: null }
    } catch (error: any) {
      if (this.ctx.shouldThrowOnError) throw error
      return { data: null, error: toStorageError(error) }
    }
  }
}
